import React, { useState, useEffect } from "react";
import { Table, Button, Dropdown, Menu } from "antd";
import { Link } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import Swal from "sweetalert2";
import { Avatar_02,Avatar_01 } from "../../../../Routes/ImagePath";


const UsersTable = () => {
  const [data, setData] = useState([]);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const cdnUrl = process.env.REACT_APP_CDN_URL;


  useEffect(() => {
    const fetchDevicesAndUsers = async () => {
      try {
        // Fetch devices
        const devicesResponse = await axios.get(`${apiUrl}/devices/devicelist`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const activeDevices = devicesResponse.data.filter(
          (device) => device.status === 1
        );
        const deviceOptions = activeDevices.map((device) => ({
          value: device.device_sr_no,
          label: device.device_desc,
        }));
        setDevices(deviceOptions);
        setSelectedDevice(deviceOptions[0] || null);

        // Fetch device users
        const usersResponse = await axios.get(`${apiUrl}/devices/deviceusers`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const enrichedUsers = usersResponse.data.data.map((user, index) => ({
          ...user,
          id: index + 1,
          synced: !!user.enrollment_id, // Synced if enrollment_id exists
          image: Avatar_01, // Placeholder avatar
        }));
        setData(enrichedUsers);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDevicesAndUsers();
  }, [apiUrl, token]);

  const handleDeviceChange = (selectedOption) => {
    setSelectedDevice(selectedOption);
  };

  const handleCheckboxChange = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSyncToggle = async (user) => {
    if (!selectedDevice) {
      Swal.fire("Error", "Please select a device before syncing/unsyncing.", "error");
      return;
    }

    const action = user.synced ? "unsync" : "sync";
    const payload = {
      cmd: user.synced ? "removeuser" : "adduser",
      sn: selectedDevice.value,
      user: {
        userid: user.enrollment_id || user.emp_id, // Use emp_id if enrollment_id is missing
        name: user.emp_name,
        privilege: user.privilege.toLowerCase(),
        password: "1234", // Default password
        card: "123456789", // Example card data
        fingerprint: "template_string_here", // Placeholder fingerprint template
      },
    };

    try {
      await axios.post(`${apiUrl}/devices/${action}-device-users`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      Swal.fire(
        "Success",
        `User ${user.emp_name} has been ${action}ed successfully!`,
        "success"
      );

      // Update sync status in the table
      setData((prevData) =>
        prevData.map((item) =>
          item.emp_id === user.emp_id ? { ...item, synced: !item.synced } : item
        )
      );
    } catch (error) {
      console.error(`Error during ${action}:`, error);
      Swal.fire("Error", `Failed to ${action} user. Please try again.`, "error");
    }
  };

  const handleAssign = async () => {
    if (!selectedDevice) {
      Swal.fire("Error", "Please select a device before assigning users.", "error");
      return;
    }

    const selectedUserDetails = data.filter((user) =>
      selectedUsers.includes(user.emp_id)
    );

    try {
      for (const user of selectedUserDetails) {
        const payload = {
          cmd: "adduser",
          sn: selectedDevice.value,
          user: {
            userid: user.enrollment_id || user.emp_id,
            name: user.emp_name,
            privilege: user.privilege.toLowerCase(),
            password: "1234",
            card: "123456789",
            fingerprint: "template_string_here",
          },
        };

        await axios.post(`${apiUrl}/devices/add-device-users`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      Swal.fire("Success", "Users assigned successfully!", "success");
      setSelectedUsers([]); // Clear selected users
    } catch (error) {
      console.error("Error assigning users:", error);
      Swal.fire("Error", "Failed to assign users. Please try again.", "error");
    }
  };

  const handleUnassign = async () => {
    if (!selectedDevice) {
      Swal.fire("Error", "Please select a device before unassigning users.", "error");
      return;
    }

    try {
      const selectedUserDetails = data.filter((user) =>
        selectedUsers.includes(user.emp_id)
      );

      for (const user of selectedUserDetails) {
        const payload = {
          cmd: "removeuser",
          sn: selectedDevice.value,
          user: {
            userid: user.enrollment_id,
          },
        };

        await axios.post(`${apiUrl}/devices/remove-device-users`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      Swal.fire("Success", "Users unassigned successfully!", "success");
      setSelectedUsers([]); // Clear selected users
    } catch (error) {
      console.error("Error unassigning users:", error);
      Swal.fire("Error", "Failed to unassign users. Please try again.", "error");
    }
  };

  const handlePrivilegeChange = async (user, newPrivilege) => {
    try {
      const payload = {
        emp_id: user.emp_id,
        privilege: newPrivilege,
      };
      await axios.post(`${apiUrl}/devices/change-privilege`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      Swal.fire("Success", `Privilege updated to ${newPrivilege}!`, "success");
      setData((prevData) =>
        prevData.map((item) =>
          item.emp_id === user.emp_id
            ? { ...item, privilege: newPrivilege }
            : item
        )
      );
    } catch (error) {
      console.error("Error changing privilege:", error);
      Swal.fire("Error", "Failed to change privilege. Please try again.", "error");
    }
  };

  const handleChangePassword = (user) => {
    Swal.fire({
      title: "Enter new password",
      input: "password",
      inputLabel: "New Password",
      inputPlaceholder: "Enter new password",
      inputAttributes: {
        maxlength: 20,
        autocapitalize: "off",
        autocorrect: "off",
      },
      showCancelButton: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const payload = {
            emp_id: user.emp_id,
            password: result.value,
          };
          await axios.post(`${apiUrl}/devices/change-password`, payload, {
            headers: { Authorization: `Bearer ${token}` },
          });
          Swal.fire("Success", "Password changed successfully!", "success");
        } catch (error) {
          console.error("Error changing password:", error);
          Swal.fire("Error", "Failed to change password. Please try again.", "error");
        }
      }
    });
  };

  const columns = [
    {
      title: "Select",
      render: (text, record) => (
        <input
          type="checkbox"
          checked={selectedUsers.includes(record.emp_id)}
          onChange={() => handleCheckboxChange(record.emp_id)}
        />
      ),
    },
    {
      title: "Name",
      dataIndex: "emp_name",
      render: (text, record) => (
            <span className="table-avatar">
              <Link to={`/profile/${record.id}`} className="avatar">
                <img 
      src={record.profile_pic 
            ? `${cdnUrl}/${record.profile_pic}` 
            : (record.gender === 'male' ? Avatar_01 : Avatar_02)
          }
      alt="Employee Avatar"
    />


          </Link>
          <Link to={`/profile/${record.id}`}>
            {text} <span>{record.lastname}</span>
          </Link>
        </span>
      ),
      sorter: (a, b) => a.emp_name.localeCompare(b.emp_name),
    },
    {
      title: "EMP ID",
      dataIndex: "empcode",
      sorter: (a, b) => a.empcode.localeCompare(b.empcode),
    },
    {
      title: "Synced",
      dataIndex: "synced",
      render: (text, record) => (
        <div className="status-toggle">
          <input
            type="checkbox"
            id={`sync_${record.emp_id}`}
            className="check"
            checked={record.synced}
            onChange={() => handleSyncToggle(record)}
          />
          <label htmlFor={`sync_${record.emp_id}`} className="checktoggle">
            checkbox
          </label>
        </div>
      ),
    },
    {
      title: "Privilege",
      dataIndex: "privilege",
      sorter: (a, b) => a.privilege.localeCompare(b.privilege),
    },
    {
      title: "Action",
      render: (text, record) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="1"
                onClick={() =>
                  handlePrivilegeChange(
                    record,
                    record.privilege === "admin" ? "user" : "admin"
                  )
                }
              >
                {record.privilege === "admin" ? "Make User" : "Make Admin"}
              </Menu.Item>
              <Menu.Item key="2" onClick={() => handleChangePassword(record)}>
                Change Password
              </Menu.Item>
            </Menu>
          }
          trigger={["click"]}
        >
          <Link to="#" className="action-icon dropdown-toggle">
            <i className="material-icons">more_vert</i>
          </Link>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="row filter-row space">
          <div className="col-sm-6 col-md-3">
            <div className="input-block form-focus select-focus">
              <Select
                placeholder="Select Device"
                value={selectedDevice}
                onChange={handleDeviceChange}
                options={devices}
                className="select floating"
              />
              <label className="focus-label">Device</label>
            </div>
          </div>
          {selectedUsers.length > 0 && (
            <div className="col-sm-6 col-md-3">
              <Button type="primary" onClick={handleAssign}>
                Assign
              </Button>
              <Button type="danger" onClick={handleUnassign} style={{ marginLeft: 10 }}>
                Unassign
              </Button>
            </div>
          )}
        </div>
        <div className="table-responsive">
          <Table
            className="table-striped"
            style={{ overflowX: "auto" }}
            columns={columns}
            dataSource={data}
            rowKey={(record) => record.emp_id}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default UsersTable;
